import { DeleteForever, ModeEdit } from "@mui/icons-material";
import { Autocomplete, Box, Button, Chip, IconButton, TextField, Typography, useMediaQuery } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useRef, useState } from "react";
import { modalDeleteCompany } from "../../../atoms/atom";
import { ExternalCompanyService } from "../../../services/ExternalCompanyService";
import { ExternalCompanyDTO } from "../../../types/ExternalCompanyDTO";
import { useUtils } from "../../../utils/utilFunctions";
import { useAlert } from "../../contextProviders/AlertProvider";
import ModalDeleteCompany from "../remove-external-company/ModalDeleteCompany";
import './ExternalCompanies.scss';

const ExternalCompanies: FC = () => {
	const [openNew, setOpenNew] = useState(false);
	const [deleteId, setDeleteId] = useAtom(modalDeleteCompany);
	const { formatDocument } = useUtils();
	const { showAlert, showError } = useAlert();
	const service = new ExternalCompanyService();
	const containerRef = useRef<HTMLDivElement>(null);
	const [companies, setCompanies] = useState<ExternalCompanyDTO[]>([]);
	const emptyCompany = {
		id: null,
		cnpj: "",
		name: "",
		cpfs: []
	};
	const [newCompany, setNewCompany] = useState<ExternalCompanyDTO>(emptyCompany);
	const allowSaveNew = newCompany.name && newCompany.cnpj && newCompany.cpfs.length;
	const isMobile = useMediaQuery('(max-width: 768px)');

	const handleChange = (field: keyof ExternalCompanyDTO, value: string | string[] | boolean) => {
		setNewCompany(prev => ({
		...prev,
		[field]: value
		}));
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
		event.preventDefault();
		const pastedText = event.clipboardData.getData('text');
		const newCpfs = extractCpfs(pastedText);
		if (newCpfs.length > 0) {
			const uniqueValues = new Set([...newCompany.cpfs, ...newCpfs]);
			handleChange('cpfs', Array.from(uniqueValues));
		}
	};

	const handleScrollToTop = () => {
		if (containerRef.current) {
			containerRef.current.scrollTo({
				top: 0,
				behavior: "smooth"
			});
		}
	};

	useEffect(() => {
		const load = async () => {
			const response = await service.getAll();
			if (response?.data) {
				setCompanies(response.data);
			}
		};
		if (!deleteId) {
			load();
		}
	}, [openNew, deleteId]);

	return (
		<Box ref={containerRef} className="external-companies">
			<Button
				id="add-company"
				variant="contained"
				color="success"
				onClick={() => setOpenNew(true)}
				sx={{display: openNew ? "none !important" : ""}}
			>
				Adicionar nova empresa externa
			</Button>
			<Box sx={{display: openNew ? "" : "none !important"}} className="inputs-new-company">
				<TextField
					value={newCompany.cnpj}
					onChange={(e) => handleChange("cnpj", formatDocument(e.target.value))}
					label="CPF/CNPJ da empresa"
					sx={{width: isMobile ? '100%' : '190px'}}
				/>
				<TextField
					value={newCompany.name}
					onChange={(e) => handleChange("name", e.target.value)}
					label="Nome da empresa"
					fullWidth
				/>
			</Box>
			<Box sx={{display: openNew ? "" : "none !important"}} className="inputs-new-company">
				<Autocomplete
					multiple
					freeSolo
					fullWidth
					options={[]}
					value={newCompany.cpfs}
					onChange={(event, newValue) => {
						handleChange('cpfs', newValue as string[]);
					}}
					renderTags={(value: readonly string[], getTagProps) =>
						value.map((option: string, index: number) => (
							<Chip
								variant="outlined"
								label={option}
								{...getTagProps({ index })}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Cole a lista de CPFs aqui"
							placeholder="CPFs..."
							helperText="Aceita dados do Excel, CSV ou texto corrido."
							onPaste={handlePaste}
						/>
					)}
				/>
			</Box>
			<Button
				variant="contained"
				color="success"
				sx={{display: allowSaveNew ? "" : "none !important"}}
				onClick={saveExternalCompany}
			>
				Salvar
			</Button>
			{companies.length
				? <div className="external-list">
					<Typography variant="h6">Empresas cadastradas</Typography>
					<div className="list">
						<div className="company-item header">
							<Typography className="item-column" sx={{fontWeight: "bold"}}>Nome</Typography>
							<Typography className="item-column" sx={{fontWeight: "bold"}}>CNPJ</Typography>
							<Typography className="item-column company-actions" sx={{fontWeight: "bold"}}>Ações</Typography>
						</div>
						{companies.map((company) => (
							<div key={company.id} className="company-item">
								<p data-label="Nome" className="item-column">{company.name}</p>
								<p data-label="CNPJ" className="item-column">{company.cnpj}</p>
								<div className="company-actions item-column">
									<IconButton
										color="inherit"
										onClick={() => {
											handleScrollToTop();
											setNewCompany(company);
											setOpenNew(true);
										}}
									>
										<ModeEdit/>
									</IconButton>
									<IconButton
										color="inherit"
										onClick={() => setDeleteId(company.id)}
									>
										<DeleteForever sx={{ color: "#e15734db" }}/>
									</IconButton>
								</div>
							</div>
						))}
					</div>
				</div>
				: <></>
			}
			<ModalDeleteCompany/>
		</Box>
	);

	async function saveExternalCompany() {
		const response = await service.create(newCompany);
		if (response?.status === 200) {
			showAlert('Empresa registrada com sucesso');
			setOpenNew(false);
			setNewCompany(emptyCompany);
		} else {
			showError();
		}
	}

	function extractCpfs(text: string): string[] {
		const regex = /(?:\d{3}\.\d{3}\.\d{3}-\d{2})|(?:\d{11})/g;
		return text.match(regex) || [];
	}
}

export default ExternalCompanies;